import React, { useEffect, useMemo, useState } from "react";
import { useModalManager } from "../../hooks/useModalManager";
import EditProductsModal from "../../modals/edit-product";
import DeleteProductModal from "../../modals/delete-product";
import Toolbar from "./toolbar";
import ProductTable from "./table";
import ScrollToTopButton from "../../components/scrolltotop";
import EmptyStateComponent from "../../components/empty";
import {
  deleteProduct,
  fetchProducts,
  updateProduct,
  getMerchantCategories,
} from "../../api/axios/requests";
import { toast } from "react-toastify";
import { useScrollToTop } from "../../hooks/userScrollToTop";

export default function MerchantProductsEditScreen() {
  const { closeModal, currentModal, openModal } = useModalManager();
  const { showScrollButton, scrollToTop } = useScrollToTop();

  const [allItems, setAllItems] = useState([]);
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);

  const [editingProduct, setEditingProduct] = useState(null);
  const [search, setSearch] = useState("");

  // ✅ toolbar filter state
  const [selectedCategory, setSelectedCategory] = useState(null);       // parent
  const [selectedSubCategory, setSelectedSubCategory] = useState(null); // child

  const [loadingProductId, setLoadingProductId] = useState();
  const [saving, setSaving] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [rawCategories, setRawCategories] = useState([]); // tree

  const [categories, setCategories] = useState([
    { value: null, label: "Бүх ангилал" },
  ]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      setCategoriesError(null);

      const res = await getMerchantCategories();
      if (!res?.success) throw new Error(res?.message || "Ангилал татаж чадсангүй");

      const tree = Array.isArray(res.data) ? res.data : [];
      setRawCategories(tree);

      const categoryOptions = tree.map((cat) => ({
        value: String(cat.id),
        label: cat.name,
      }));
      setCategories([{ value: null, label: "Бүх ангилал" }, ...categoryOptions]);
    } catch (error) {
      console.error("Fetch categories error:", error);
      setCategoriesError("Ангилал татахад алдаа гарлаа.");
      setCategories([{ value: "", label: "Бүх ангилал" }]);
      setRawCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // ✅ parent сонгосон үед front filter хийх id-ууд:
  // parent өөрөө + хүүхдүүд (хоёр төрлийн data холилдсон ч бүгд гарна)
  const parentFilterIds = useMemo(() => {
    if (!selectedCategory) return null;
    const parent = rawCategories.find((p) => String(p.id) === String(selectedCategory));
    if (!parent) return [Number(selectedCategory)];

    const childrenIds = (parent.children || []).map((c) => Number(c.id));
    return [Number(parent.id), ...childrenIds];
  }, [selectedCategory, rawCategories]);

  const applyFrontFilter = (list) => {
    // ✅ sub сонгосон бол front дээр шүүх хэрэггүй (backend яг зөв шүүнэ)
    if (selectedSubCategory) return list;

    if (!selectedCategory) return list;

    const ids = parentFilterIds || [];
    return (list || []).filter((p) => ids.includes(Number(p.categoryId)));
  };

  // ✅ Backend өөрчлөхгүйгээр:
  // - sub сонгосон үед categoryId=subId явуулна
  // - бусад үед categoryId явуулахгүй (null) + front filter
  const loadProducts = async (pageToLoad = 1, isRefresh = false) => {
    try {
      setLoading(true);
      setLoadError(false);

      const result = await fetchProducts({
        page: pageToLoad,
        categoryId: selectedSubCategory ? selectedSubCategory : null,
        keyword: search,
      });

      const chunk = Array.isArray(result.data) ? result.data : [];

      if (isRefresh) {
        setAllItems(chunk);
        setItems(applyFrontFilter(chunk));
      } else {
        setAllItems((prev) => {
          const merged = [...prev, ...chunk];
          setItems(applyFrontFilter(merged));
          return merged;
        });
      }

      // pagination байхгүй бол (хуучин backend) аюулгүй уншина.
      const pg = result.pagination || {};
      setHasMore((pg.currentPage || pageToLoad) < (pg.totalPages || 0));
      setPage(pg.currentPage || pageToLoad);
    } catch (error) {
      console.error("Fetch products error:", error);
      if (isRefresh) setLoadError(true); // сүлжээ/серверийн алдаа — «дата алга» биш
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
    loadProducts(1, true);
  }, []);

  // ✅ search өөрчлөгдвөл дахин татна
  useEffect(() => {
    setAllItems([]);
    setItems([]);
    loadProducts(1, true);
  }, [search, selectedSubCategory]); // ✅ sub өөрчлөгдвөл backend шүүнэ

  // ✅ parent өөрчлөгдвөл backend татахгүй, зөвхөн front filter
  useEffect(() => {
    setItems(applyFrontFilter(allItems));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, parentFilterIds]);

  const handleLoadMore = () => {
    if (hasMore && !loading) loadProducts(page + 1, false);
  };

  const handleVisible = async (e, productId) => {
    const newVisible = e.target.checked;
    try {
      setLoadingProductId(productId);
      const res = await updateProduct(productId, { isVisible: newVisible });
      // {success:false} хариуг амжилттай гэж харуулахгүй.
      if (res?.success === false) throw new Error(res?.message || "Алдаа");

      setAllItems((prev) =>
        prev.map((p) => (p.id === productId ? { ...p, isVisible: newVisible } : p))
      );
      setItems((prev) =>
        prev.map((p) => (p.id === productId ? { ...p, isVisible: newVisible } : p))
      );

      toast.success(res.message || "Бараа амжилттай шинэчлэгдлээ.");
    } catch (error) {
      console.error("Product visible update error:", error);
      toast.error("Барааг шинэчлэх үед алдаа гарлаа.");
    } finally {
      setLoadingProductId(null);
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    openModal("edit-product");
  };

  const handleSaveProduct = async (updatedData) => {
    if (!editingProduct?.id) {
      toast.error("Барааны ID олдсонгүй.");
      return;
    }

    try {
      setSaving(true);
      setLoadingProductId(editingProduct.id);

      const res = await updateProduct(editingProduct.id, updatedData);

      setAllItems((prev) =>
        prev.map((p) => (p.id === editingProduct.id ? { ...p, ...res.product } : p))
      );
      setItems((prev) =>
        prev.map((p) => (p.id === editingProduct.id ? { ...p, ...res.product } : p))
      );

      toast.success(res.message || "Бараа амжилттай шинэчлэгдлээ.");
    } catch (error) {
      console.error("Product update error:", error);
      toast.error("Барааг шинэчлэх үед алдаа гарлаа.");
    } finally {
      setSaving(false);
      setLoadingProductId(null);
      closeModal();
      setEditingProduct(null);
      // ⚠️ Өмнө нь энд `setItems(applyFrontFilter(allItems))` байсан нь ХУУЧИН
      //    (засахаас өмнөх) allItems closure-ыг ашиглаж, сая зассан мөрийг буцаан
      //    дарж бичдэг тул засвар «наалддаггүй» байв. try доторх setItems аль
      //    хэдийн зөв шинэчилсэн тул энд дахин apply хийх шаардлагагүй — устгав.
    }
  };

  const handleRefetch = () => {
    setAllItems([]);
    setItems([]);
    loadProducts(1, true);
  };

  const isEmpty = !loading && items.length === 0;

  const handleDeleteProduct = (product) => {
    setDeletingProduct(product);
    openModal("delete-product");
  };

  const confirmDeleteProduct = async () => {
    if (!deletingProduct?.id) {
      toast.error("Устгах барааны ID олдсонгүй.");
      return;
    }
    try {
      setDeleting(true);
      const res = await deleteProduct(deletingProduct.id);
      if (res?.success === false) throw new Error(res?.message || "Алдаа");

      setAllItems((prev) => prev.filter((p) => p.id !== deletingProduct.id));
      setItems((prev) => prev.filter((p) => p.id !== deletingProduct.id));

      toast.success("Бараа амжилттай устгагдлаа.");
    } catch (error) {
      console.error(error);
      toast.error("Барааг устгах үед алдаа гарлаа.");
    } finally {
      setDeleting(false);
      setDeletingProduct(null);
      closeModal();
    }
  };

  return (
    <div className="container-fluid app">
      <div className="row">
        <div className="col-12">
          <Toolbar
            search={search}
            setSearch={setSearch}
            selectedCategory={selectedCategory}
            handleCategoryChange={(v) => setSelectedCategory(v)}
            selectedSubCategory={selectedSubCategory}
            handleSubCategoryChange={(v) => setSelectedSubCategory(v)}
            categories={categories}
            rawCategories={rawCategories}
            loadingCategories={loadingCategories}
            categoriesError={categoriesError}
            onRefetch={handleRefetch}
          />
          <h4 className="page-title mt-3">Бараа засах</h4>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body pt-1">
              {loadError && !loading && items.length === 0 ? (
                <div className="text-center py-5">
                  <i className="uil uil-exclamation-triangle text-danger" style={{ fontSize: 40 }} />
                  <p className="mt-2 mb-3">Бараа татахад алдаа гарлаа.</p>
                  <button className="btn btn-outline-primary" onClick={handleRefetch}>
                    Дахин оролдох
                  </button>
                </div>
              ) : isEmpty ? (
                <EmptyStateComponent
                  title={"Бараа байхгүй байна"}
                  subtitle={"Та одоогоор ямар нэг бараа бүртгээгүй байна"}
                />
              ) : (
                <ProductTable
                  items={items}
                  loading={loading}
                  hasMore={hasMore}
                  onEndReached={handleLoadMore}
                  loadingProductId={loadingProductId}
                  handleVisible={handleVisible}
                  onEdit={handleEditProduct}
                  onDelete={handleDeleteProduct}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <EditProductsModal
        show={currentModal === "edit-product"}
        handleClose={() => {
          closeModal();
          setEditingProduct(null);
        }}
        initialData={editingProduct || {}}
        categories={rawCategories}
        onSave={handleSaveProduct}
        saving={saving}
      />

      <DeleteProductModal
        show={currentModal === "delete-product"}
        handleClose={() => {
          closeModal();
          setDeletingProduct(null);
        }}
        product={deletingProduct || {}}
        onDelete={confirmDeleteProduct}
        deleting={deleting}
      />

      {showScrollButton && <ScrollToTopButton onClick={scrollToTop} />}
    </div>
  );
}
